import { ReactNode, useEffect } from "react";
import { useNavigate } from "react-router";
import { useLocation } from "react-router";
import {
  IsStripeCustomer,
  List,
  ListElement,
  BuyerOnly,
  SupplierOnly,
} from "../../components";
import { IconType } from "@sellar-io/components";
import { AdminOnly } from "../../components/authFilter/AdminOnly";
import { useDashboardContext } from "../../components/dashboard/Dashboard.shared";
import { NavigationBarMenu } from "../../components/navigation/NavigationBar";
import {
  ACCOUNT,
  ACCOUNT_NOTIFICATION_SETTINGS,
  ACCOUNT_SETTINGS,
  BUSINESS,
  BUSINESS_FEATURE_FLAG_SETTINGS,
  BUSINESS_INTEGRATION_SETTINGS,
  BUSINESS_INVOICES_SETTINGS,
  BUSINESS_PAYMENT_METHOD_SETTINGS,
  BUSINESS_SETTINGS,
  BUSINESS_TERMS_AND_CONDITIONS,
  BUSINESS_FINANCE_CREDIT,
} from "../../constants/routes";
import { useStripePortalCreate } from "../../hooks/api/v2/stripePortal/useStripePortalCreate";
import { useRemoteFeatureFlags } from "../../hooks/useRemoteFeatureFlags";
import { useSellarUser } from "../../hooks";

export function AccountSettingsList() {
  const { signOut } = useSellarUser();
  const navigate = useNavigate();

  return (
    <SettingsContainer
      redirectOnMatchUrl={ACCOUNT}
      redirectTo={ACCOUNT_SETTINGS}
    >
      <NavigationBarMenu title="Settings" />
      <List>
        <SettingsListElement to={ACCOUNT_SETTINGS}>
          Your Account
        </SettingsListElement>
        <SettingsListElement
          to={""}
          onClick={() =>
            navigate(ACCOUNT_NOTIFICATION_SETTINGS, {
              state: {
                source: "Settings menu",
              },
            })
          }
        >
          Notifications
        </SettingsListElement>
        <SupplierOnly>
          <IsStripeCustomer>
            <Billing />
          </IsStripeCustomer>
        </SupplierOnly>
        <SettingsListElement
          icon="EXIT_TO_APP"
          iconClassName="text-red-500"
          onClick={() => {
            signOut();
          }}
        >
          Sign out
        </SettingsListElement>
      </List>
    </SettingsContainer>
  );
}

export function BusinessSettingsList() {
  const invoicingEnabled = useRemoteFeatureFlags().isActive("invoicing");
  return (
    <SettingsContainer
      redirectOnMatchUrl={BUSINESS}
      redirectTo={BUSINESS_SETTINGS}
    >
      <NavigationBarMenu title="Settings" />
      <List>
        <SettingsListElement to={BUSINESS_SETTINGS}>
          Business Details
        </SettingsListElement>
        <BuyerOnly>
          <SettingsListElement to={BUSINESS_PAYMENT_METHOD_SETTINGS}>
            Payment Methods
          </SettingsListElement>
        </BuyerOnly>
        <SupplierOnly>
          <SettingsListElement to={BUSINESS_INTEGRATION_SETTINGS}>
            Integrations
          </SettingsListElement>
          {invoicingEnabled && (
            <SettingsListElement to={BUSINESS_INVOICES_SETTINGS}>
              Invoices
            </SettingsListElement>
          )}
          <SettingsListElement to={BUSINESS_FINANCE_CREDIT}>
            Finances
          </SettingsListElement>
          <IsStripeCustomer>
            <Billing />
          </IsStripeCustomer>
          <SettingsListElement to={BUSINESS_TERMS_AND_CONDITIONS}>
            Terms and conditions
          </SettingsListElement>
        </SupplierOnly>
        <AdminOnly>
          <SettingsListElement to={BUSINESS_FEATURE_FLAG_SETTINGS}>
            Feature Flags
          </SettingsListElement>
        </AdminOnly>
      </List>
    </SettingsContainer>
  );
}

const Billing = () => {
  const stripePortalMutation = useStripePortalCreate();

  const redirectToStripePortal = async () => {
    if (!stripePortalMutation.isPending) {
      try {
        const stripePortalSession = await stripePortalMutation.mutateAsync();
        window.open(stripePortalSession.url, "_blank", "noreferrer");
      } catch (error) {
        console.error(error);
      }
    }
  };

  const icon = stripePortalMutation.isPending
    ? "TIMER_SAND_EMPTY"
    : "OPEN_IN_NEW";

  const listText = stripePortalMutation.isPending
    ? "Redirecting..."
    : "Billing";

  return (
    <SettingsListElement icon={icon} onClick={redirectToStripePortal}>
      {listText}
    </SettingsListElement>
  );
};

type SettingsContainerProps = {
  redirectOnMatchUrl: string;
  redirectTo: string;
  children: ReactNode;
};
export const SettingsContainer = ({
  redirectOnMatchUrl,
  redirectTo,
  children,
}: SettingsContainerProps) => {
  const location = useLocation();
  const { isTwoPanel } = useDashboardContext();
  const navigate = useNavigate();

  useEffect(() => {
    if (isTwoPanel && location.pathname === redirectOnMatchUrl) {
      navigate(redirectTo);
    }
  }, [location.pathname, isTwoPanel, navigate, redirectOnMatchUrl, redirectTo]);

  return children;
};

const SettingsListElement = ({
  to,
  icon = "CHEVRON_RIGHT",
  iconClassName,
  onClick,
  children,
}: {
  to?: string;
  icon?: keyof typeof IconType;
  iconClassName?: string;
  onClick?: () => void;
  children: ReactNode;
}) => {
  const location = useLocation();

  return (
    <ListElement
      to={to}
      selected={location.pathname === to}
      icon={icon}
      iconClassName={iconClassName}
      onClick={onClick}
    >
      {children}
    </ListElement>
  );
};
